import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type ReactNode
} from "react"

export type HeaderLocationPickerContextValue = {
  requestOpenHeaderLocationPicker: () => boolean
  registerHeaderLocationPickerOpener: (open: () => void) => () => void
}

const HeaderLocationPickerContext =
  createContext<HeaderLocationPickerContextValue | null>(null)

export function HeaderLocationPickerProvider({
  children
}: {
  children: ReactNode
}) {
  const openerRef = useRef<(() => void) | null>(null)

  const registerHeaderLocationPickerOpener = useCallback((open: () => void) => {
    openerRef.current = open
    return () => {
      if (openerRef.current === open) openerRef.current = null
    }
  }, [])

  const requestOpenHeaderLocationPicker = useCallback(() => {
    if (!openerRef.current) return false
    openerRef.current()
    return true
  }, [])

  const value = useMemo(
    () => ({
      requestOpenHeaderLocationPicker,
      registerHeaderLocationPickerOpener
    }),
    [registerHeaderLocationPickerOpener, requestOpenHeaderLocationPicker]
  )

  return (
    <HeaderLocationPickerContext.Provider value={value}>
      {children}
    </HeaderLocationPickerContext.Provider>
  )
}

export function useHeaderLocationPicker(): HeaderLocationPickerContextValue | null {
  return useContext(HeaderLocationPickerContext)
}
